
import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cx } from '@client/shared/lib/cx.ts';
import { useCurrentUser, useHasRole } from '@client/shared/auth';
import {
  SlotGrid,
  cellLabelForState,
  type CellState,
  type SlotGridEvent,
} from '@client/shared/calendar/slot-grid';
import { Calendar } from '@client/shared/calendar/Calendar.tsx';
import { calendarLegendLabel } from '@client/shared/calendar/text.ts';
import { cancelBooking } from '../cancel-booking-operation.ts';
import { toBookingDisplay } from '../booking-display.ts';
import { EventDetailCard, BookingDetailBody } from './EventDetailCard.tsx';
import { useAgentNameMap } from '../agent-operations.ts';
import { SLOT_MINUTES } from '@shared/booking';
import { dayHeaderLabel, formatSlotLabel } from '@client/shared/lib/wall-clock-intake.ts';
import { instantRangeToGridSpans } from '@client/shared/calendar/booking-grid.ts';
import { formatListingRange, listingZoneLabel } from './listing-zone-time.ts';
import {
  editUnavailabilityReason,
  removeUnavailability,
} from './edit-unavailability-operation.ts';
import { UnavailabilityBlock } from './UnavailabilityBlock.tsx';
import { SlotActionCard } from './SlotActionCard.tsx';
import { listingDetailsKey } from './listing-details-operations.ts';
import type { ListingDetail } from './listing-details-operations.ts';
import { toUnavailabilityBlocks } from './listing-details-operations.ts';
import {
  useBookingIntake,
  VISIBLE_START_SLOT,
  VISIBLE_END_SLOT,
} from './use-booking-intake.ts';
import styles from './ListingDetails.module.css';

export interface BookingCalendarProps {
  listing: ListingDetail;
  canBlock?: boolean;
  canBookForOthers?: boolean;
  /** Fires once each time a booking lands (used to complete the first-run tutorial). */
  onBooked?: () => void;
}

export function BookingCalendar({
  listing,
  canBlock = false,
  canBookForOthers = false,
  onBooked,
}: BookingCalendarProps) {
  const queryClient = useQueryClient();
  const zone = listing.timezone ?? null;
  const intake = useBookingIntake({ listing, canBlock, canBookForOthers, onBooked });
  const { days, painted } = intake;

  const viewerUserId = useCurrentUser()?.user.id ?? null;
  const nameMap = useAgentNameMap();

  const isAdmin = useHasRole('admin', 'owner');
  const cancelMutation = useMutation({
    mutationFn: (bookingId: string) => cancelBooking({ bookingId }),
  });
  const { mutateAsync: cancelBookingAsync } = cancelMutation;
  const onCancelBooking = useCallback(
    async (bookingId: string) => {
      await cancelBookingAsync(bookingId);
      await queryClient.invalidateQueries({ queryKey: listingDetailsKey(listing.id ?? '') });
    },
    [cancelBookingAsync, queryClient, listing.id],
  );

  const bookingBlocks = useMemo<SlotGridEvent[]>(() => {
    const out: SlotGridEvent[] = [];
    for (const b of listing.bookings ?? []) {
      if (!b) continue;
      const display = toBookingDisplay(
        b,
        listing,
        { viewerUserId, agentNames: nameMap },
        { fallbackAddress: 'Open house' },
      );
      if (!display) continue;
      const spans = instantRangeToGridSpans(
        new Date(display.start),
        new Date(display.end),
        days,
        zone ?? '',
        { startSlot: VISIBLE_START_SLOT, endSlot: VISIBLE_END_SLOT },
      );
      if (spans.length === 0) continue;
      const timeRange = formatListingRange(new Date(display.start), new Date(display.end), zone);
      const agentRef = display.agentLabel;
      const label = `Open house, ${timeRange}${agentRef ? `, ${agentRef}` : ''}`;
      const canCancel = isAdmin || display.owner === 'mine';
      spans.forEach((span) => {
        out.push({
          id: `${display.id}:${span.dayIndex}`,
          dayIndex: span.dayIndex,
          startSlot: span.startSlot,
          endSlot: span.endSlot,
          tone: display.owner,
          interactive: true,
          content: (
            <BookingBlock
              tone={display.owner}
              address={display.address}
              timeRange={timeRange}
              agentRef={agentRef}
              ariaLabel={label}
              canCancel={canCancel}
              onCancelBooking={() => onCancelBooking(display.id)}
            />
          ),
        });
      });
    }
    return out;
  }, [listing, days, zone, viewerUserId, nameMap, isAdmin, onCancelBooking]);

  const refetchListing = useCallback(
    () => queryClient.invalidateQueries({ queryKey: listingDetailsKey(listing.id ?? '') }),
    [queryClient, listing.id],
  );

  const onEditBlockReason = useCallback(
    async (unavailabilityId: string, reason: string) => {
      await editUnavailabilityReason({ unavailabilityId, reason });
      await refetchListing();
    },
    [refetchListing],
  );
  const onRemoveBlock = useCallback(
    async (unavailabilityId: string) => {
      await removeUnavailability({ unavailabilityId });
      await refetchListing();
    },
    [refetchListing],
  );

  const unavailabilityEvents = useMemo<SlotGridEvent[]>(() => {
    const out: SlotGridEvent[] = [];
    for (const u of toUnavailabilityBlocks(listing)) {
      const spans = instantRangeToGridSpans(new Date(u.start), new Date(u.end), days, zone ?? '', {
        startSlot: VISIBLE_START_SLOT,
        endSlot: VISIBLE_END_SLOT,
      });
      if (spans.length === 0) continue;
      const inline = u.reason && u.reason.trim() !== '' ? u.reason : calendarLegendLabel.blocked;
      spans.forEach((span) => {
        out.push({
          id: `unavail:${u.id}:${span.dayIndex}`,
          dayIndex: span.dayIndex,
          startSlot: span.startSlot,
          endSlot: span.endSlot,
          tone: 'blocked',
          interactive: true,
          label: `Blocked: ${inline}`,
          content: (
            <UnavailabilityBlock
              reason={u.reason}
              createdAt={u.createdAt}
              zone={zone}
              start={u.start}
              end={u.end}
              canEdit={canBlock}
              onEditReason={(reason) => onEditBlockReason(u.id, reason)}
              onRemove={() => onRemoveBlock(u.id)}
            />
          ),
        });
      });
    }
    return out;
  }, [listing, days, zone, canBlock, onEditBlockReason, onRemoveBlock]);

  const overlayEvents = useMemo<SlotGridEvent[]>(
    () => [...bookingBlocks, ...unavailabilityEvents],
    [bookingBlocks, unavailabilityEvents],
  );

  const cellLabel = (day: number, slot: number, state: CellState) =>
    cellLabelForState(
      state,
      dayHeaderLabel(days[day] ?? '', zone ?? undefined),
      formatSlotLabel(slot * SLOT_MINUTES),
      'selected',
    );

  return (
    <>
      <Calendar
        anchor={intake.anchor}
        zone={zone ?? 'UTC'}
        range={intake.range}
        onStep={intake.step}
        onToday={intake.goToToday}
        canStepBack={intake.canStepBack}
        canStepForward={intake.canStepForward}
        onRangeChange={intake.rangeForced ? undefined : intake.setRange}
        legend={['selection', 'yourBooking', 'otherAgents', 'blocked']}
      >
        <div ref={intake.gridWrapRef}>
          <SlotGrid
            ariaLabel="Booking calendar"
            columns={intake.columns}
            rows={intake.rows}
            painted={painted}
            onChange={intake.paint}
            onPaintEnd={intake.release}
            cellState={intake.cellState}
            cellLabel={cellLabel}
            events={overlayEvents}
            zoneLabel={listingZoneLabel(zone)}
          />
        </div>
      </Calendar>

      {intake.card && <SlotActionCard ref={intake.cardRef} {...intake.card} />}
    </>
  );
}

function BookingBlock({
  tone,
  address,
  timeRange,
  agentRef,
  ariaLabel,
  canCancel,
  onCancelBooking,
}: {
  tone: 'mine' | 'other';
  address: string;
  timeRange: string;
  agentRef: string | null;
  ariaLabel: string;
  canCancel: boolean;
  onCancelBooking: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={styles.bookingTrigger}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={cx(styles.blockTime, 'tnum')}>{timeRange}</span>
        {agentRef && <span className={styles.blockSub}>{agentRef}</span>}
      </button>

      {open && (
        <EventDetailCard
          anchorRef={triggerRef}
          tone={tone}
          eyebrow="Open house"
          ariaLabel="Open house"
          onClose={() => setOpen(false)}
        >
          <BookingDetailBody
            title={address}
            timeRange={timeRange}
            meta={agentRef ?? ''}
            note={
              canCancel
                ? 'This frees the slot for another agent to book. Anyone notified about this listing will be told it was cancelled.'
                : 'Only an admin or owner can cancel another agent’s booking.'
            }
            canCancel={canCancel}
            onCancel={onCancelBooking}
            onClose={() => setOpen(false)}
          />
        </EventDetailCard>
      )}
    </>
  );
}
